// Saving a picture or video from a URL into the member's photo library.
//
// Lifted out of EventPage/ImageCarousel.vue so the lesson images (Next Steps)
// save the same way an event photo does: into the app's own album on Android,
// straight into the library on iOS, with one retry. The carousel keeps its own toasts, counters and Storyblok web proxy;
// this module is only the part that talks to Photos.
import { Media } from '@capacitor-community/media';
import { isPlatform } from '@ionic/vue';

export const PHOTOS_ALBUM_NAME = 'HOGC One';

// Photos can only be written to from the native apps. The web build (and the
// app served in a mobile browser) has no photo library to save into; callers
// fall back to a browser download or an outbound link there.
export function canSaveToPhotos() {
  return (isPlatform('android') || isPlatform('ios')) && !isPlatform('mobileweb');
}

// The app's own album (Android only), created on first save. If it is missing but other albums
// exist, the first of those is used instead — carousel behaviour, kept as is.
export async function getOrCreateAlbum() {
  const { albums } = await Media.getAlbums();
  let album = albums.find(a => a.name === PHOTOS_ALBUM_NAME);

  if (!album && albums.length > 0) {
    album = albums[0];
  }

  if (!album) {
    await Media.createAlbum({ name: PHOTOS_ALBUM_NAME });
    const { albums: newAlbums } = await Media.getAlbums();
    album = newAlbums.find(a => a.name === PHOTOS_ALBUM_NAME);
  }

  return album;
}

// iOS saves straight into the library, with no album. Listing or creating an
// album needs full photo library access; a save with no album needs only
// "Add Photos Only", the narrower prompt. Android's plugin rejects a save with
// no album, and needs no permission for it (androidGalleryMode is off). On
// Android 10+ our patch to the plugin (patches/@capacitor-community+media*)
// saves into the shared library as Pictures/<album> or Movies/<album>, using
// only the album's name; Android 9 keeps the app's own media folder.
async function buildSaveOptions(url) {
  if (isPlatform('ios')) return { path: url };
  const album = await getOrCreateAlbum();
  return { path: url, albumIdentifier: album.identifier };
}

// Saves the file at `url` into Photos. One retry after 500 ms: the first call
// straight after the permission grant has been seen to fail, and the second
// succeed. Rejects with the plugin's error when both fail.
export async function saveToPhotos(url, { isVideo = false, retry = true } = {}) {
  const saveFn = isVideo ? Media.saveVideo : Media.savePhoto;
  try {
    return await saveFn(await buildSaveOptions(url));
  } catch (error) {
    console.error(`[save-to-photos] save${isVideo ? 'Video' : 'Photo'} error:`, error);
    if (retry) {
      await new Promise(resolve => setTimeout(resolve, 500));
      return saveToPhotos(url, { isVideo, retry: false });
    }
    throw error;
  }
}
